import { Box } from '@mui/material';
import { useRef, useEffect } from 'react';
import { Header, HeaderBreadcrumb } from 'common/ui/Header';
import { Form, FormRef } from 'common/form';
import {
  campaignCreationSharedJobInformationFields,
  campaignCreationSharedJobInformationGroups,
  campaignCreationSharedJobInformationSchema,
  CampaignCreationSharedJobInformationFormData
} from 'modules/campaign-creation-shared/forms';
import { scrollToTop } from 'common/utils';

type CampaignCreationSharedJobInformationProps = {
  title?: string;
  subtitle?: string;
  currentBreadcrumb: string;
  breadcrumbs: HeaderBreadcrumb[];
  onBack: () => void;
  actions?: React.ReactNode;
  defaultValues: CampaignCreationSharedJobInformationFormData;
  onSubmit: (values: CampaignCreationSharedJobInformationFormData) => void;
  submitButtonLabel?: string;
  onFormChange?: (values: CampaignCreationSharedJobInformationFormData) => void;
};

export const CampaignCreationSharedJobInformation: React.FC<
  CampaignCreationSharedJobInformationProps
> = ({
  title,
  subtitle,
  currentBreadcrumb,
  breadcrumbs,
  onBack,
  actions,
  defaultValues,
  onSubmit,
  submitButtonLabel = 'Next step',
  onFormChange
}) => {
  const formRef =
    useRef<FormRef<CampaignCreationSharedJobInformationFormData>>(null);

  useEffect(() => {
    if (formRef.current && onFormChange) {
      const subscription = formRef.current.watch(
        (values: CampaignCreationSharedJobInformationFormData) => {
          onFormChange(values);
        }
      );
      return () => subscription?.unsubscribe?.();
    }
  }, [onFormChange]);

  const displayTitle = defaultValues?.jobTitle || title || currentBreadcrumb;

  const handleSubmit = (
    values: CampaignCreationSharedJobInformationFormData
  ) => {
    scrollToTop();
    onSubmit(values);
  };

  return (
    <Box>
      <Header
        title={displayTitle}
        subtitle={subtitle}
        currentBreadcrumb={currentBreadcrumb}
        breadcrumbs={breadcrumbs}
        onBack={onBack}
        actions={actions}
      />
      <Form
        ref={formRef}
        schema={campaignCreationSharedJobInformationSchema}
        fields={campaignCreationSharedJobInformationFields}
        groups={campaignCreationSharedJobInformationGroups}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        submitButtonProps={{
          label: submitButtonLabel
        }}
        buttonContainerSx={{ mt: 3 }}
      />
    </Box>
  );
};
